import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Plus, Pencil, Trash2, Palette, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

export default function ColorsPage() {
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color_code: "#000000" });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => { fetchColors(); }, []);

  const fetchColors = async () => {
    try {
      const { data } = await axios.get(`${API}/colors`, { headers: getAuthHeaders(), withCredentials: true });
      setColors(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openAdd = () => { setEditing(null); setForm({ name: "", color_code: "#000000" }); setFormError(""); setShowForm(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, color_code: c.color_code }); setFormError(""); setShowForm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError("");
    try {
      if (editing) {
        await axios.put(`${API}/colors/${editing.id}`, form, { headers: getAuthHeaders(), withCredentials: true });
      } else {
        await axios.post(`${API}/colors`, form, { headers: getAuthHeaders(), withCredentials: true });
      }
      setShowForm(false);
      fetchColors();
    } catch (err) {
      setFormError(err.response?.data?.detail || "Something went wrong.");
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this color?")) return;
    try {
      await axios.delete(`${API}/colors/${id}`, { headers: getAuthHeaders(), withCredentials: true });
      fetchColors();
    } catch (err) { console.error(err); }
  };

  return (
    <div data-testid="page-colors" className="min-h-screen bg-[#F4F3EF]">
      <div className="bg-white border-b border-[#161616] px-5 sm:px-8 py-6 gap-4 flex-wrap flex items-center justify-between">
        <div>
          <p className="text-xs font-bold text-[#5A5955] mb-1">Master Data</p>
          <h1 className="text-3xl font-bold tracking-[-0.02em] text-[#161616]">Colors</h1>
        </div>
        <button onClick={openAdd} data-testid="add-color-btn"
          className="inline-flex items-center gap-2 bg-[#161616] text-white font-bold px-5 py-2.5 text-sm hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_#00A3E0] transition-all duration-200">
          <Plus size={16} /> Add Color
        </button>
      </div>

      <div className="px-5 sm:px-8 py-6">
        {loading ? (
          <div className="text-center py-12 text-[#5A5955]">Loading...</div>
        ) : colors.length === 0 ? (
          <div className="bg-white border border-[#D9D7D0] p-12 text-center">
            <Palette size={36} className="mx-auto text-[#D9D7D0] mb-4" />
            <p className="text-sm text-[#5A5955]">No colors yet. Add one to get started.</p>
          </div>
        ) : (
          <div className="bg-white border border-[#D9D7D0] overflow-x-auto">
            <table className="w-full text-left" data-testid="colors-table">
              <thead>
                <tr className="border-b border-[#D9D7D0]">
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Color</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Name</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Code</th>
                  <th className="px-6 py-4 text-xs font-bold text-[#5A5955]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {colors.map((c) => (
                  <tr key={c.id} className="border-b border-[#D9D7D0] hover:bg-[#F7F6F2] transition-colors">
                    <td className="px-6 py-4">
                      <div className="w-8 h-8 border border-[#D9D7D0]" style={{ backgroundColor: c.color_code }} />
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-[#161616]">{c.name}</td>
                    <td className="px-6 py-4 text-sm font-mono text-[#5A5955]">{c.color_code}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEdit(c)} className="p-2 hover:bg-[#F4F3EF] transition-colors"><Pencil size={14} className="text-[#5A5955]" /></button>
                        <button onClick={() => handleDelete(c.id)} className="p-2 hover:bg-red-50 transition-colors"><Trash2 size={14} className="text-red-500" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && createPortal(
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="color-modal">
            <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-[#161616]">
              <h2 className="text-xl font-bold tracking-tight text-[#161616]">{editing ? "Edit Color" : "Add New Color"}</h2>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-[#F4F3EF]"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-8 space-y-5">
              <div>
                <label className="field-label">Name *</label>
                <input type="text" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="color-input-name"
                  className="field" placeholder="e.g. Navy Blue" />
              </div>
              <div>
                <label className="field-label">Color Code *</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={form.color_code} onChange={(e) => setForm({...form, color_code: e.target.value})} data-testid="color-input-picker"
                    className="w-12 h-12 cursor-pointer border border-[#D9D7D0]" />
                  <input type="text" required value={form.color_code} onChange={(e) => setForm({...form, color_code: e.target.value})} data-testid="color-input-code"
                    className="field !w-auto flex-1 font-mono" placeholder="#000000" />
                </div>
              </div>
              {formError && <p className="text-sm text-red-600 bg-red-50 px-4 py-2 border border-red-200">{formError}</p>}
              <button type="submit" disabled={submitting} data-testid="submit-color-btn"
                className="w-full bg-[#161616] text-white font-bold px-8 py-4 text-sm hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_#00A3E0] transition-all duration-200 disabled:opacity-50">
                {submitting ? "Saving..." : editing ? "Update Color" : "Add Color"}
              </button>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
